import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Lock, Loader2, Eye, EyeOff, ArrowLeft, CheckCircle2, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import useSeo from "@/hooks/useSeo";

export default function ResetPassword() {
  useSeo({
    title: "Nuova password",
    description: "Imposta una nuova password per il tuo account Autofattura. Min 8 caratteri, cifratura bcrypt lato server.",
    path: "/reset-password",
    noindex: true,
  });
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) toast.error("Link di reset non valido");
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) return toast.error("La password deve essere di almeno 8 caratteri");
    if (password !== confirm) return toast.error("Le password non coincidono");
    setLoading(true);
    try {
      await api.post("/auth/reset-password/confirm", { token, new_password: password });
      setDone(true);
      setTimeout(() => navigate("/login"), 2500);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Il link non è valido o è scaduto");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-[#FAF9F6] p-6" data-testid="reset-password-page">
      <div className="w-full max-w-md bg-white rounded-2xl border border-[#E8E4D9] p-8 shadow-sm">
        <Link to="/login" className="inline-flex items-center gap-1 text-sm text-[#4A4A4A] hover:text-[#0A0A0A] mb-6">
          <ArrowLeft className="w-3.5 h-3.5" /> Torna al login
        </Link>
        {done ? (
          <div className="text-center" data-testid="reset-done-panel">
            <div className="mx-auto w-14 h-14 rounded-full bg-emerald-50 border border-emerald-200 grid place-items-center mb-4">
              <CheckCircle2 className="w-7 h-7 text-emerald-600" />
            </div>
            <h1 className="font-display font-extrabold text-3xl tracking-tighter">Password aggiornata</h1>
            <p className="text-[#4A4A4A] mt-3 text-sm">Ti stiamo reindirizzando al login…</p>
          </div>
        ) : !token ? (
          <div className="text-center" data-testid="reset-invalid-panel">
            <div className="mx-auto w-14 h-14 rounded-full bg-red-50 border border-red-200 grid place-items-center mb-4">
              <AlertTriangle className="w-7 h-7 text-red-600" />
            </div>
            <h1 className="font-display font-extrabold text-3xl tracking-tighter">Link non valido</h1>
            <p className="text-[#4A4A4A] mt-3 text-sm">Richiedi un nuovo link dalla pagina di login.</p>
          </div>
        ) : (
          <>
            <h1 className="font-display font-extrabold text-3xl tracking-tighter">
              Nuova{" "}
              <span className="font-serif-accent text-[#1D4ED8]">password</span>
            </h1>
            <p className="text-[#4A4A4A] mt-2 text-sm">Almeno 8 caratteri.</p>
            <form onSubmit={submit} className="mt-6 space-y-4">
              <PwField value={password} onChange={setPassword} label="Nuova password" testid="reset-password-input" show={show} setShow={setShow} />
              <PwField value={confirm} onChange={setConfirm} label="Conferma password" testid="reset-confirm-input" show={show} setShow={setShow} />
              <button type="submit" disabled={loading} data-testid="reset-submit-btn"
                className="w-full h-11 rounded-full bg-[#0A0A0A] text-white font-semibold text-sm hover:bg-[#1D4ED8] disabled:opacity-70 transition-colors inline-flex items-center justify-center gap-2">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Aggiorna password"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

function PwField({ value, onChange, label, testid, show, setShow }) {
  return (
    <div>
      <label className="block text-sm font-medium text-[#0A0A0A] mb-1.5">{label}</label>
      <div className="relative">
        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#737373]" />
        <input type={show ? "text" : "password"} required value={value} onChange={(e) => onChange(e.target.value)}
          data-testid={testid} minLength={8}
          className="w-full h-11 pl-9 pr-10 rounded-md border border-[#E8E4D9] bg-white focus:border-[#0A0A0A] focus:ring-2 focus:ring-[#0A0A0A]/10 outline-none text-sm"
          placeholder="••••••••" />
        <button type="button" onClick={() => setShow((v) => !v)} tabIndex={-1}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-[#737373] hover:text-[#0A0A0A]">
          {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}
